import browserDetect from 'browser-detect';
import { generateBrowserID } from '~/utils/platform-utils';

const REFERRAL_PREFIX = 'mitra_referral_';
const YIR_PREFIX = 'yearinreview_';
const AGENLITE_PREFIX = 'agenlite_';
const CSM_PREFIX = 'csm_';

const trackerMixin = {
  beforeRouteEnter(_to, from, next) {
    next(vm => {
      vm.prevRoute = from;
    });
  },
  methods: {
    toSnakeCase(str) {
      if (typeof str === 'undefined') return '';
      let transformedStr = `${str}`
        .replace(/[^\w\s]/gi, ' ')
        .split(/\s+|_+|(?=[A-Z])/)
        .join('_')
        .toLowerCase();
      if (transformedStr[0] === '_') transformedStr = transformedStr.slice(1, transformedStr.length);
      return transformedStr;
    },
    getMitraStatus(store) {
      if (!store.getters['auth/isUserLoggedIn']) {
        return 'non-login';
      }
      return 'mitra';
    },
    generateVisitParams(store, moreParams) {
      const isFirstPage = window.history.length <= 1;
      const params = {
        is_first_journey: isFirstPage,
        user_type: this.getMitraStatus(store),
        ...moreParams,
      };
      return Object.keys(params)
        .map(k => (typeof params[k] === 'number' || !!params[k] ? `${k}=${params[k]}` : null))
        .filter(p => !!p)
        .join('&');
    },
    generateBantuanEventClickParams(params) {
      return Object.keys(params)
        .map(k => (typeof params[k] === 'number' || !!params[k] ? `${k}=${params[k]}` : null))
        .filter(p => !!p)
        .join('&');
    },
    track(data, useBeacon) {
      const browser = browserDetect();
      const payload = {
        ...data,
        browser: browser.name,
        browser_version: browser.version,
        identity: generateBrowserID(),
      };

      // handle tracker requests before navigating to a different page
      // especially to avoid NS_BINDING_ABORTED error in Firefox
      if (useBeacon && navigator?.sendBeacon && !!this.$tracker.$config.baseUrl) {
        navigator.sendBeacon(
          `${this.$tracker.$config.baseUrl}/e`,
          JSON.stringify({ ...payload })
        );
      } else {
        this.$tracker.event(payload);
      }
    },
    // New Tracker Data
    newTrackActions(data, useBeacon) {
      const { getJourneyId, getCurrentUserId36 } = this.$store.getters;
      const referrer = {
        name: this.$route.query.from || this.prevRoute?.name || '',
        url: this.prevRoute?.name ? this.prevRoute.path : document.referrer,
      };
      const trackingData = Object.assign(
        {},
        {
          journey_id: getJourneyId,
          ui: getCurrentUserId36,
          referrer_screen: referrer.name,
          referrer_url: referrer.url,
        },
        data
      );
      this.track(trackingData, useBeacon);
    },
    trackYIRPagesVisit(_event, { action, screen_name: screenName }) {
      const { getCurrentUserId36 } = this.$store.getters;
      this.track({
        evn: `${YIR_PREFIX}event_page_visited`,
        action,
        screen_name: screenName,
        ui: getCurrentUserId36,
      });
    },
    trackYIRClick(_event, { click_source: clickSource }) {
      const { getCurrentUserId36 } = this.$store.getters;
      const { name } = this.$route;
      this.track({
        evn: `${YIR_PREFIX}click`,
        click_source: clickSource,
        referrer: name,
        ui: getCurrentUserId36,
      });
    },
    trackScreenVisit({ current_screen: currentScreen, params = {} } = {}) {
      const { name } = this.$route;
      this.newTrackActions({
        evn: `${AGENLITE_PREFIX}screen_visit`,
        current_screen: currentScreen || name,
        params: this.generateVisitParams(this.$store, params) || '',
      });
    },
    trackEventClick(clickSource, { value, target_screen: targetScreen } = {}) {
      const { name } = this.$route;
      const payload = {
        evn: `${AGENLITE_PREFIX}event_click`,
        current_screen: name,
        click_source: clickSource,
      };
      if (value) payload.click_source_status = this.toSnakeCase(value);
      if (targetScreen) payload.target_screen = targetScreen;
      this.newTrackActions(payload);
    },
    trackFreebiesScreenVisit(_event, { current_screen: currentScreen, referrer_screen: referrerScreen }) {
      this.newTrackActions({
        evn: `${AGENLITE_PREFIX}screen_visit`,
        current_screen: currentScreen,
        referrer_screen: referrerScreen,
      });
    },
    trackFreebiesEventClick(
      _event,
      { click_source: clickSource, current_screen: currentScreen, referrer_screen: referrerScreen }
    ) {
      this.newTrackActions({
        evn: `${AGENLITE_PREFIX}event_click`,
        current_screen: currentScreen,
        referrer_screen: referrerScreen,
        click_source: clickSource,
      });
    },
    trackBantuanEventClick({
      user = {},
      click_source: clickSource,
      current_screen: currentScreen,
      click_source_status: clickSourceStatus,
      params = {}
    }, useBeacon) {
      this.newTrackActions({
        evn: `${AGENLITE_PREFIX}hubungi_bukalapak_click`,
        current_screen: currentScreen || '',
        user_em: user.email || '',
        user_name: user.name || '',
        user_type: user.type || '',
        click_source: clickSource,
        click_source_status: clickSourceStatus || 'default',
        params: this.generateBantuanEventClickParams(params)
      }, useBeacon);
    },
    trackBukaBantuanLiveChat(data) {
      const { getCurrentUserId36 } = this.$store.getters;
      const payload = {
        evn: `live_chat_mitra`,
        ui: getCurrentUserId36 || '',
        entry_point: data.entryPoint,
      };
      this.track(payload);
    },

    /**
     * Track Mitra change phone number KTP validation event
     * @param {object} param
     * @param {boolean} param.success - is KTP validation succeeded or failed
     * @param {string} param.failedReason
     */
    trackCpnKtpValidation({ success = false, failedReason = '' }) {
      const { getCurrentUserId36 } = this.$store.getters;
      const payload = {
        evn: `${CSM_PREFIX}cpn_ktp_validation`,
        ui: getCurrentUserId36 || '',
        is_logged_in: !!getCurrentUserId36,
        success,
        failed_reason: failedReason,
      };

      this.$tracker.event(payload);
    },

    /**
     * Track Mitra change phone number Selfie validation event
     * @param {object} param
     * @param {boolean} param.success - is Selfie validation succeeded or failed
     * @param {string} param.failedReason
     */
    trackCpnSelfieValidation({ success = false, failedReason = '' }) {
      const { getCurrentUserId36 } = this.$store.getters;
      const payload = {
        evn: `${CSM_PREFIX}cpn_selfie_validation`,
        ui: getCurrentUserId36 || '',
        is_logged_in: !!getCurrentUserId36,
        success,
        failed_reason: failedReason,
      };

      this.$tracker.event(payload);
    },

    /**
     * Track Mitra change phone number Supporting Document validation event
     * @param {object} param
     * @param {boolean} param.success - is Supporting Document validation succeeded or failed
     * @param {string} param.failedReason
     */
    trackCpnSupportingDocumentValidation({ success = false, failedReason = '' }) {
      const { getCurrentUserId36 } = this.$store.getters;
      const payload = {
        evn: `${CSM_PREFIX}cpn_supporting_document_validation`,
        ui: getCurrentUserId36 || '',
        is_logged_in: !!getCurrentUserId36,
        success,
        failed_reason: failedReason,
      };

      this.$tracker.event(payload);
    },

    /**
     * Track Mitra change phone number Credential Changes history checking event
     * @param {object} param
     * @param {boolean} param.eligible - is user credential change history eligible
     * @param {string} param.failedReason
     */
    trackCpnCheckCredentialChangeEligibility({ eligible = false, failedReason = '' }) {
      const { getCurrentUserId36 } = this.$store.getters;
      const payload = {
        evn: `${CSM_PREFIX}cpn_account_precheck_cred_change`,
        ui: getCurrentUserId36 || '',
        eligible,
        failed_reason: failedReason,
      };

      this.$tracker.event(payload);
    },

    /**
     * Track Mitra change phone number Wallet balance checking event
     * @param {object} param
     * @param {boolean} param.eligible - is user Wallet balance eligible
     * @param {number} param.amount - The GMV amount the user has
     * @param {string} param.failedReason
     */
    trackCpnCheckWalletEligibility({ eligible = false, amount = 0, failedReason = '' }) {
      const { getCurrentUserId36 } = this.$store.getters;
      const payload = {
        evn: `${CSM_PREFIX}cpn_account_precheck_wallet`,
        ui: getCurrentUserId36 || '',
        eligible,
        amount,
        failed_reason: failedReason,
      };

      this.$tracker.event(payload);
    },

    /**
     * Track Mitra change phone number's auto change phone number function event
     * @param {object} param
     * @param {boolean} param.success - is user auto change phone number function succeeded
     * @param {string} param.failedReason
     */
    trackCpnAutoChangePhoneNumberAction({ success = false, failedReason = '' }) {
      const { getCurrentUserId36 } = this.$store.getters;
      const payload = {
        evn: `${CSM_PREFIX}cpn_auto_change_phone_number`,
        ui: getCurrentUserId36 || '',
        success,
        failed_reason: failedReason,
      };

      this.$tracker.event(payload);
    },
  },
};

export default trackerMixin;
